import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { addAlertNote } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageAlerts } from '../lib/familyAlerts';
import { useAlertText, alertSubject, isMaintenance } from '../lib/alertText';
import { AlertClosure } from './alerts/AlertClosure';
import { StatusChip, SeverityDot } from './StatusChip';
import { useRelativeTime, formatDateTime } from '../lib/time';
import {
  EnvelopeSimple, WhatsappLogo, ChatCircle, CheckCircle, XCircle,
  Clock, Phone, ArrowRight, NotePencil, PlusCircle, Wrench,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CHANNEL_ICON = { email: EnvelopeSimple, whatsapp: WhatsappLogo, sms: ChatCircle };

function deliveryVisual(status) {
  if (status === 'delivered') return { Icon: CheckCircle, cls: 'text-sage-600' };
  if (status === 'sent') return { Icon: CheckCircle, cls: 'text-sage-500' };
  if (status === 'failed') return { Icon: XCircle, cls: 'text-terracotta-700' };
  return { Icon: Clock, cls: 'text-ink-muted' };
}

export function statusVisual(status) {
  if (status === 'new') return 'critical';
  if (status === 'acknowledged') return 'warning';
  return 'ok';
}

export function AlertCard({ alert: a, patient, onAck, onResolve, onReopen, onOpenTimeline, onNoteAdded }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canManage = useCanManageAlerts(a);
  const rel = useRelativeTime();
  const text = useAlertText();
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const phone = patient?.emergency_contact_phone;

  const submitNote = async () => {
    const v = noteText.trim();
    if (!v) return;
    try {
      await addAlertNote(a.id, v);
      toast.success(t('note_saved'));
      setNoteText('');
      setNoteOpen(false);
      onNoteAdded?.();
    } catch { toast.error(t('generic_error')); }
  };

  return (
    <li
      data-testid={`alert-${a.id}`}
      className="bg-white border border-stone_line rounded-2xl p-6 shadow-soft animate-fade-up"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <SeverityDot severity={a.severity} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-medium text-ink" data-testid={`alert-subject-${a.id}`}>{alertSubject(a, t)}</div>
            {isMaintenance(a) && (
              <span data-testid={`alert-maintenance-${a.id}`} className="inline-flex items-center gap-1 text-[11px] text-ink-soft bg-bg-alt border border-stone_line rounded-full px-2 py-0.5">
                <Wrench size={12} weight="duotone" /> {t('al_tab_maint')}
              </span>
            )}
            <StatusChip status={statusVisual(a.status)} label={t(`status_${a.status}`)} />
            {a.notes_count > 0 && (
              <span data-testid={`alert-notes-count-${a.id}`} className="inline-flex items-center gap-1 text-xs text-ink-soft bg-bg-alt rounded-full px-2 py-0.5">
                <ChatCircle size={12} weight="duotone" /> {a.notes_count}
              </span>
            )}
            <span className="text-xs text-ink-muted">{formatDateTime(a.created_at, lang)} · {rel(a.created_at)}</span>
          </div>
          <div className="text-sm text-ink-soft mt-2" data-testid={`alert-text-${a.id}`}>{text(a)}</div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold">
              {t('delivery_status')}:
            </span>
            {(Array.isArray(a.deliveries) ? a.deliveries : []).map((d) => {
              const CIcon = CHANNEL_ICON[d.channel] || EnvelopeSimple;
              const { Icon, cls } = deliveryVisual(d.status);
              return (
                <span
                  key={d.channel}
                  className="inline-flex items-center gap-1.5 bg-bg-alt rounded-full px-3 py-1 text-xs"
                  data-testid={`delivery-${a.id}-${d.channel}`}
                >
                  <CIcon size={14} className="text-ink-soft" />
                  <span className="text-ink capitalize">{d.channel}</span>
                  <Icon size={14} className={cls} weight="fill" />
                </span>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={() => onOpenTimeline(a.id)}
              data-testid={`alert-timeline-${a.id}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-sage/30 bg-sage-100 text-sage-700 hover:bg-sage hover:text-white text-xs font-medium transition-colors"
            >
              <Clock size={14} weight="bold" />
              {t('alert_view_timeline')}
            </button>
            {phone && (
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                data-testid={`alert-call-${a.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-sage/30 bg-sage-100 text-sage-600 hover:bg-sage hover:text-white text-xs font-medium transition-colors"
              >
                <Phone size={14} weight="fill" />
                {t('call_contact')} · {phone}
              </a>
            )}
            {a.resident_uuid && (
              <Link
                to={`/patients/${a.resident_uuid}`}
                data-testid={`alert-open-${a.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone_line bg-white text-ink-soft hover:bg-bg-alt hover:text-ink text-xs font-medium transition-colors"
              >
                {t('view_resident')}
                <ArrowRight size={14} weight="bold" />
              </Link>
            )}
            {canManage && (
              <button
                onClick={() => { setNoteOpen((v) => !v); setNoteText(''); }}
                data-testid={`alert-note-${a.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone_line bg-white text-ink-soft hover:bg-bg-alt hover:text-ink text-xs font-medium transition-colors"
              >
                <NotePencil size={14} />
                {t('add_note')}
              </button>
            )}
          </div>

          {noteOpen && (
            <div className="mt-3 flex gap-2" data-testid={`alert-note-form-${a.id}`}>
              <input
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitNote()}
                placeholder={t('note_placeholder')}
                data-testid={`alert-note-input-${a.id}`}
                className="flex-1 px-3 py-2 rounded-lg border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 text-sm"
              />
              <button
                onClick={submitNote}
                data-testid={`alert-note-save-${a.id}`}
                className="px-3 py-2 rounded-lg bg-sage text-white hover:bg-sage-600 text-sm transition-colors"
              >
                {t('save_note')}
              </button>
            </div>
          )}

          {Array.isArray(a.notes) && a.notes.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid={`alert-notes-${a.id}`}>
              {a.notes.map((n, idx) => (
                <li key={idx} className="text-xs text-ink-soft bg-bg-alt rounded-lg px-3 py-2 inline-flex items-start gap-2 max-w-full">
                  <PlusCircle size={12} className="text-sage-600 mt-0.5 shrink-0" weight="duotone" />
                  <span className="min-w-0">
                    <span className="text-ink">{n.note}</span>
                    <span className="text-ink-muted ml-2">· {rel(n.at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <AlertClosure alert={a} testid={`alert-closure-${a.id}`} />
        </div>

        <div className="flex flex-col gap-2">
          {canManage && !a.acknowledged && a.status === 'new' && (
            <button
              onClick={() => onAck(a.id)}
              data-testid={`alert-ack-${a.id}`}
              className="bg-sage text-white hover:bg-sage-600 px-4 py-2 rounded-xl text-sm transition-colors"
            >
              {t('acknowledge')}
            </button>
          )}
          {canManage && a.status !== 'resolved' && (
            <button
              onClick={() => onResolve(a.id)}
              data-testid={`alert-resolve-${a.id}`}
              className="border border-sage/40 text-sage-700 hover:bg-sage-100 px-4 py-2 rounded-xl text-sm transition-colors"
            >
              {t('resolve')}
            </button>
          )}
          {canManage && a.status === 'resolved' && (
            <button
              onClick={() => onReopen(a.id)}
              data-testid={`alert-reopen-${a.id}`}
              className="border border-stone_line text-ink-soft hover:bg-bg-alt px-4 py-2 rounded-xl text-sm transition-colors"
            >
              {t('reopen')}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
